import { useCallback, useEffect, useMemo, useState } from 'react'

import { useDevicePermissionStates } from './use_device_permission_states'

export type DevicesManagerType = {
  initialized: boolean
  audioDevices: { [id: string]: MediaDeviceInfo }
  videoDevices: { [id: string]: MediaDeviceInfo }
  audioOutputDevices: { [id: string]: MediaDeviceInfo }
  hasAudioDevices: boolean
  hasVideoDevices: boolean
  hasAudioOutputDevices: boolean
  askForPermission:
    | ((options: { video?: boolean; audio?: boolean }) => Promise<boolean>)
    | undefined
  permissionStates: {
    [key: string]: PermissionStatus['state'] | undefined
  }
  invalidateDevices: (() => void) | undefined
  speakerSupport: boolean
  pendingPermissionRequests: { camera: boolean; microphone: boolean }
  getUserMedia: typeof navigator.mediaDevices.getUserMedia
}

type AccType = {
  audioDevices: { [id: string]: MediaDeviceInfo }
  videoDevices: { [id: string]: MediaDeviceInfo }
  audioOutputDevices: { [id: string]: MediaDeviceInfo }
  hasAudioDevices: boolean
  hasVideoDevices: boolean
  hasAudioOutputDevices: boolean
}

export const useDevicesManager = () => {
  const [audioDevices, setAudioDevices] = useState<{
    [id: string]: MediaDeviceInfo
  }>({})
  const [videoDevices, setVideoDevices] = useState<{
    [id: string]: MediaDeviceInfo
  }>({})
  const [audioOutputDevices, setAudioOutputDevices] = useState<{
    [id: string]: MediaDeviceInfo
  }>({})
  const [initialized, setInitialized] = useState(false)

  const [hasAudioDevices, setHasAudioDevices] = useState<boolean>(false)
  const [hasVideoDevices, setHasVideoDevices] = useState<boolean>(false)
  const [hasAudioOutputDevices, setHasAudioOutputDevices] =
    useState<boolean>(false)
  const [devicesInvalid, setDevicesInvalid] = useState(false)
  const { permissionStates } = useDevicePermissionStates()
  // inferredPermissionStates: for the browsers that don't support querying the permission states of microphone and camera
  const [inferredPermissionStates, setInferredPermissionStates] = useState<{
    [key: string]: PermissionStatus['state'] | undefined
  }>({ camera: undefined, microphone: undefined })
  const [pendingPermissionRequests, setPendingPermissionRequests] = useState({
    camera: false,
    microphone: false,
  })

  const speakerSupport = useMemo(() => {
    const el = new Audio()
    // eslint-disable-next-line
    // @ts-ignore
    const supported = !!el.setSinkId
    el.remove()
    return supported
  }, [])

  const refreshAndSetDevices = useCallback(async () => {
    const result = await refreshDeviceList()
    setAudioDevices(result.audioDevices)
    setVideoDevices(result.videoDevices)
    setAudioOutputDevices(result.audioOutputDevices)
    setHasAudioDevices(result.hasAudioDevices)
    setHasVideoDevices(result.hasVideoDevices)
    setHasAudioOutputDevices(result.hasAudioOutputDevices)
    setInitialized(true)
  }, [])

  useEffect(() => {
    const devicesCanBeEnumerated = navigator && navigator.mediaDevices
    if (!devicesCanBeEnumerated) {
      return
    }

    refreshAndSetDevices()

    // ondevicechange is not supported using the cordova InAppBrowser, but is supported by other modern browsers and devices
    // so, conditionally bind to this event
    const browserSupportsOnDeviceChange =
      'ondevicechange' in navigator.mediaDevices
    if (browserSupportsOnDeviceChange) {
      navigator.mediaDevices.addEventListener(
        'devicechange',
        refreshAndSetDevices,
      )
    }

    return () => {
      if (browserSupportsOnDeviceChange) {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          refreshAndSetDevices,
        )
      }
    }
  }, [refreshAndSetDevices])

  useEffect(() => {
    if (devicesInvalid) {
      refreshAndSetDevices().then(() => {
        setDevicesInvalid(false)
      })
    }
  }, [devicesInvalid, refreshAndSetDevices])

  useEffect(() => {
    setDevicesInvalid(true)
  }, [permissionStates])

  // avoid using this function as it will temporarily create a stream, prefer using getUserMedia wrapper below
  const askForPermissionWrapper = useCallback(
    ({ video = false, audio = false }) => {
      setPendingPermissionRequests({ camera: video, microphone: audio })
      return askForPermission({ video, audio })
        .then(
          (success) => {
            setInferredPermissionStates((prevState) => {
              return {
                ...prevState,
                ...(video && { camera: success ? 'granted' : 'denied' }),
                ...(audio && { microphone: success ? 'granted' : 'denied' }),
              }
            })
            setDevicesInvalid(true)
            return Promise.resolve(success)
          },
          (result) => {
            return Promise.reject(result)
          },
        )
        .finally(() => {
          setPendingPermissionRequests({ camera: false, microphone: false })
        })
    },
    [],
  )

  const getUserMedia: typeof navigator.mediaDevices.getUserMedia =
    useCallback((constraints) => {
      const video = !!constraints?.video
      const audio = !!constraints?.audio
      setPendingPermissionRequests({ camera: video, microphone: audio })
      return navigator.mediaDevices
        .getUserMedia(constraints)
        .then(
          (success) => {
            setInferredPermissionStates((prevState) => {
              return {
                ...prevState,
                ...(video && { camera: success ? 'granted' : 'denied' }),
                ...(audio && { microphone: success ? 'granted' : 'denied' }),
              }
            })
            setDevicesInvalid(true)
            return Promise.resolve(success)
          },
          (result) => {
            return Promise.reject(result)
          },
        )
        .finally(() => {
          setPendingPermissionRequests({ camera: false, microphone: false })
        })
    }, [])

  const invalidateDevices = useCallback(() => {
    setDevicesInvalid(true)
  }, [])

  const pStates = useMemo(() => {
    return {
      ...inferredPermissionStates,
      ...permissionStates,
    }
  }, [inferredPermissionStates, permissionStates])

  return useMemo(() => {
    return {
      initialized,
      audioDevices,
      videoDevices,
      audioOutputDevices,
      hasAudioDevices,
      hasVideoDevices,
      hasAudioOutputDevices,
      askForPermission: askForPermissionWrapper,
      permissionStates: pStates,
      invalidateDevices,
      speakerSupport,
      pendingPermissionRequests,
      getUserMedia,
    }
  }, [
    askForPermissionWrapper,
    audioDevices,
    audioOutputDevices,
    getUserMedia,
    hasAudioDevices,
    hasAudioOutputDevices,
    hasVideoDevices,
    initialized,
    invalidateDevices,
    pStates,
    pendingPermissionRequests,
    speakerSupport,
    videoDevices,
  ])
}

const refreshDeviceList = () =>
  (navigator.mediaDevices.enumerateDevices() || Promise.resolve([])).then(
    (mediaDevices) => {
      return mediaDevices.reduce(
        (acc: AccType, mediaDevice) => {
          const { deviceId, kind } = mediaDevice
          // check if deviceId is truthy, as we do not want to show devices blocked by permissions
          if (deviceId) {
            switch (kind) {
              case 'audioinput':
                acc.audioDevices[deviceId] = mediaDevice
                break
              case 'videoinput':
                acc.videoDevices[deviceId] = mediaDevice
                break
              case 'audiooutput':
                acc.audioOutputDevices[deviceId] = mediaDevice
                break
            }
          }
          switch (kind) {
            case 'audioinput':
              acc.hasAudioDevices = true
              break
            case 'videoinput':
              acc.hasVideoDevices = true
              break
            case 'audiooutput':
              acc.hasAudioOutputDevices = true
              break
          }
          return acc
        },
        {
          audioDevices: {},
          videoDevices: {},
          audioOutputDevices: {},
          hasAudioDevices: false,
          hasVideoDevices: false,
          hasAudioOutputDevices: false,
        },
      )
    },
  )

const askForPermission = (constraints: MediaStreamConstraints) => {
  return navigator.mediaDevices.getUserMedia(constraints).then(
    (stream) => {
      const tracks = stream.getTracks()
      tracks.forEach((track) => {
        track.stop()
        stream.removeTrack(track)
      })
      return Promise.resolve(true)
    },
    () => {
      return Promise.resolve(false)
    },
  )
}
